import React, {FC, memo, useEffect, useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {Button, Space, Tag} from "antd";
import moment from "moment";
import {grey, red} from "@ant-design/colors";
import Page from "../../views/Page/Page";
import PageContentWrapper from "../../views/PageContentWrapper/PageContentWrapper";
import LayoutStep from "../../views/LayoutStep/LayoutStep";
import i18n from "../../services/i18n";
import {GetBookingByIdQuery, GetBookingByIdWithCourseLessonBookingsQuery, PaymentType, useGetBookingByIdQuery, useGetBookingByIdWithCourseLessonBookingsQuery} from "../../generated/graphql";
import PanelCardCourseData from "../../views/PanelCardCourseData/PanelCardCourseData";
import PanelCardPaymentData from "../../views/PanelCardPaymentData/PanelCardPaymentData";
import PanelCardBooker from "../../views/PanelCardBooker/PanelCardBooker";
import RenderInvoicePreview from "../../views/RenderInvoicePreview/RenderInvoicePreview";
import PanelCard from "../../views/PanelCard/PanelCard";
import {LocationState} from "../../types";
import {returnBackHelper} from "../../helpers/returnBackHelper";
import {URL_BOOKINGS_LIST} from "../../models/url";
import {getBookingDetailsPath, isBookingSettled} from "../../helpers/bookingDetailsRouting";
import useHighestRole, {isAdminOrHigher} from "../../helpers/useHighestRole";
import PanelCardNotes from "../../views/PanelCardNotes/PanelCardNotes";
import PanelCardPayments from "../../views/PanelCardPayments/PanelCardPayments";
import SendMailMenu from "./SendMailMenu/SendMailMenu";
import {LeftMenuBookingIcon} from "../../assets/SVG-Icons";
import CorrectionInvoice from "./CorrectionInvoice/CorrectionInvoice";
import CorrectBookingWizard from "./SettlementDetails/CorrectBookingWizard/CorrectBookingWizard";
import {bookingHasMolliePayment} from "../../helpers/bookingHasMolliePayment";
import BookingHistoryDrawer from "../bookingHistory/BookingHistoryDrawer";
import MoreMenuSettlementDetails from "./SettlementDetails/MoreMenuSettlementDetails";
import CommunicationHistoryBooking from "../bookings/CommunicationHistoryBooking/CommunicationHistoryBooking";
import LateEntryPricingNotice from "../../views/LateEntryPricingNotice/LateEntryPricingNotice";
import ErasedRecordNotice from "../../views/ErasedRecordNotice/ErasedRecordNotice";
// Phase 23 / Plan 23-04 / BDSEC-01 — Strategy-Switch Mollie ↔ klassisch.
import {MolliePaymentSection} from "./SettlementDetails/MolliePaymentSection";
// Phase 23 / Plan 23-06 / Task 2 — Conditional Polling auf Booking-Query.
import {isActiveState} from "./SettlementDetails/MolliePaymentSection/helpers/isActiveState";
import InvoiceSentStatusTagContainer from "./SettlementDetails/InvoiceSentStatusTagContainer";
import css from "./SettlementDetails.less";
import {getDisplayedPaymentMethodType} from "../../helpers/getDisplayedPaymentMethodType";
import BookingDunningsPanel from "./SettlementDetails/BookingDunnings/BookingDunningsPanel";
import BookingFeeLettersPanel from "./SettlementDetails/FeeLetters/BookingFeeLettersPanel";

// Phase 23 / Plan 23-06 / D-06 — Polling-Interval bei aktiven Mollie-Stati.
// 30 s reicht für UX-Latenz (Webhook-Settlement passiert idR < 5 s; Polling-Tick
// ist Fallback gegen verlorene/verzögerte Webhooks). Bei finalem Status wird
// `pollInterval=0` gesetzt → Apollo stoppt das Polling automatisch.
const MOLLIE_POLL_INTERVAL = 30000;

type SettlementDetailsProps = {
    bookingId?: string;
    renderWithoutPage?: boolean;
    modalStyle?: React.CSSProperties;
    setModalOpen?: (value: boolean) => void;
};

const SettlementDetails: FC<SettlementDetailsProps> = ({bookingId, renderWithoutPage, modalStyle, setModalOpen}) => {
    const history = useHistory();
    const location = useLocation<LocationState>();
    const highestRole = useHighestRole();
    // Phase 23 / Plan 23-04: State-Type folgt jetzt der konsumierten Query
    // `GetBookingByIdWithCourseLessonBookings`, weil sie zusaetzliche
    // Felder enthaelt (`allowMolliePayment` auf Course, `molliePaymentAttempts`
    // auf Booking). Davor war der State auf den engeren `GetBookingByIdQuery`
    // gemappt — was zum Type-Mismatch in `setBooking(bookingData)` fuehrte,
    // sobald die Query um Mollie-Selection erweitert wurde.
    const [booking, setBooking] = useState<GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"] | undefined>(undefined);

    const [timeStamp, setTimeStamp] = useState<number>(0);

    // Phase 23 / Plan 23-06 / D-06 + T-23-07 — conditional Mollie-Polling.
    //
    // Apollo `pollInterval` ist dynamisch:
    //   * Mollie-Booking + currentAttempt.status ∈ {open, pending, authorized}
    //     → 30000 ms
    //   * Sonst (klassische Buchung ODER final-state attempt) → 0 (stop)
    //
    // Apollo behandelt `0` als "kein Polling" — bei laufender Subscription
    // wird der Tick deaktiviert. Initialisierung mit `0`, damit der erste
    // Render nicht versehentlich polled bevor wir die Mollie-Stati kennen.
    const [computedPollInterval, setComputedPollInterval] = useState<number | undefined>(0);

    // const {data, loading} = useGetBookingByIdQuery({
    //     variables: {id: bookingId ?? ""},
    //     fetchPolicy: "network-only",
    // });

    const {data, loading, refetch} = useGetBookingByIdWithCourseLessonBookingsQuery({
        variables: {id: bookingId ?? ""},
        fetchPolicy: "network-only",
        pollInterval: computedPollInterval,
    });

    // Pitfall 4 — Dep-Array enthaelt explizit `molliePaymentAttempts` UND
    // `courseLessons`. Wenn man nur `data` als Dep nutzt, koennte Apollo bei
    // identical reference (z.B. cache-hit ohne struct change) den Effect
    // ueberspringen — Apollo garantiert kein neues Top-Level-Object pro
    // Render-Tick. Explizite Field-Refs zwingen die useEffect-Diff-Engine,
    // den Pfad zu prüfen → Polling-Stop bei pending→paid-Übergang ist sicher.
    useEffect(() => {
        const booking = data?.bookingByIdWithCourseLessonBookings;
        // Baustein 5 (mollie-treatment-follows-method): folgt der METHODE (SP-Provider),
        // nicht mehr dem Kurs — nach Umbuchen/Methodenwechsel zeigt das Panel die echte
        // Bezahlmethode der Buchung.
        const allowMollie = bookingHasMolliePayment(booking?.scheduledPayments);

        if (!allowMollie) {
            setComputedPollInterval(0);

            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const attempts = ((booking as any)?.molliePaymentAttempts as Array<{status?: string; isCurrentAttempt?: boolean}> | undefined) ?? [];
        // Mehrere Raten → mehrere aktuelle Attempts: gepollt wird, sobald IRGENDEINER
        // davon aktiv ist (gleiche Regel wie der Polling-Hint in MolliePaymentSection).
        const active = attempts.some((a) => a.isCurrentAttempt === true && isActiveState(a.status));

        setComputedPollInterval(active ? MOLLIE_POLL_INTERVAL : 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (data?.bookingByIdWithCourseLessonBookings as any)?.molliePaymentAttempts,
        data?.bookingByIdWithCourseLessonBookings?.scheduledPayments,
    ]);

    const returnBack = (): void => {
        returnBackHelper({
            history,
            location,
            fallbackUrl: URL_BOOKINGS_LIST,
        });
    };

    // const bookingData: GetBookingByIdQuery["bookingById"] | undefined = data?.bookingById;
    const bookingData: GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"] | undefined = data?.bookingByIdWithCourseLessonBookings;

    useEffect(() => {
        setBooking(bookingData);
    }, [bookingData]);

    const paymentMethodType = booking?.paymentMethod ? getDisplayedPaymentMethodType(booking.paymentMethod) : undefined;


    // Settlement Guard: Redirect to edit page if booking is NOT settled (has no invoice)
    // Only active when NOT rendered as modal (renderWithoutPage !== true)
    useEffect(() => {
        // Guard not active in modal mode
        if (renderWithoutPage) return;
        // Wait for data to be loaded
        if (loading) return;
        if (!bookingData || !bookingId) return;

        const isSettled = isBookingSettled(bookingData);

        // If booking IS settled, stay on this page (no redirect needed)
        if (isSettled) return;

        // Booking is NOT settled -> redirect to edit page
        const allowLessonBooking = bookingData.courseLessons[0]?.course?.allowLessonBooking ?? false;

        const targetPath = getBookingDetailsPath(bookingId, isSettled, allowLessonBooking, highestRole, location.pathname);

        // Only redirect if not already on the correct page
        if (location.pathname !== targetPath) {
            history.replace(targetPath, location.state);
        }
    }, [renderWithoutPage, loading, bookingData, bookingId, highestRole, history, location.pathname, location.state]);

    // Phase 23 / Plan 23-04: Parameter-Type auf konsumierten Query-Type
    // umgestellt (war historisch `GetBookingByIdQuery` — vermutete Lese-Pfad
    // davor; tatsaechlich nutzt die Komponente seit Jahren bereits
    // `useGetBookingByIdWithCourseLessonBookings`). Pitfall-1-konforme
    // Null/Undefined-Pruefungen — `invoice` kann `undefined` oder `null` sein.
    const getInvoiceData = (
        booking: GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"],
    ) => {
        if (!booking.invoice) {
            return undefined;
        }
        if (booking.reversalInvoice) {
            const {invoiceNumberPrefix, invoiceNumber, id} = booking.reversalInvoice;

            return {
                id,
                invoiceNumber,
                invoiceNumberPrefix,
                isReversalInvoice: true,
            };
        }

        const {invoiceNumberPrefix, invoiceNumber, id} = booking.invoice;

        return {
            id,
            invoiceNumber,
            invoiceNumberPrefix,
            isReversalInvoice: false,
        };
    };

    // COLLECTING DATA ---------------------------------------------------------

    const invoiceData = booking ? getInvoiceData(booking) : undefined;

    type BookingType = "PlaceReservation" | "Booking" | "Waitlist";
    const bookingType: BookingType = booking?.bookingType ? (booking.bookingType as BookingType) : "Booking";

    // RETURN ------------------------------------------------------------------

    const isNotSettled = !isBookingSettled(booking);
    const isCanceled = booking?.reversalInvoice?.id !== undefined;
    const subTextSecondPart = isNotSettled ? "" : isCanceled ? `(${i18n.containers.invoice.InvoiceDetails.canceled()})` : `(${i18n.containers.invoice.InvoiceDetails.settled()})`;

    const InvoiceHeader = () => {
        const {correctionDate, createDate} = booking?.invoice ?? {
            correctionDate: undefined,
            createDate: undefined,
        };

        const subStyle: React.CSSProperties = {
            fontSize: "0.8rem",
            fontWeight: 400,
            color: grey[3],
            marginRight: -5,
        };

        const sentStatusTag =
            booking?.id && booking?.invoice ? (
                <InvoiceSentStatusTagContainer
                    bookingId={booking.id}
                    invoice={booking.invoice}
                    isReversal={isCanceled}
                />
            ) : null;

        if (correctionDate) {
            return (
                <Space size={20}>
                    <span>{i18n.containers.invoice.InvoiceDetails.invoice()}</span>
                    <Space size={10} style={subStyle}>
                        <span>{`Erstellt:`}</span>
                        <Tag color="green">{moment(createDate).format("L")}</Tag>
                        <span style={subStyle}>{`Korrektur:`}</span>
                        <Tag color="volcano">{moment(correctionDate).format("L")}</Tag>
                        {sentStatusTag}
                    </Space>
                </Space>
            );
        }

        return (
            <Space size={20}>
                <span>{i18n.containers.invoice.InvoiceDetails.invoice()}</span>
                <Space size={10} style={subStyle}>
                    <span>{`Erstellt`}</span>
                    <Tag color="green">{moment(createDate).format("L")}</Tag>
                    {sentStatusTag}
                </Space>
            </Space>
        );
    };

    const renderContent = () => {
        // Phase 23 / Plan 23-04 / BDSEC-01 — Strategy-Switch.
        //
        // Strikter `=== true`-Check (Pitfall 2): `allowMolliePayment` ist im
        // GraphQL-Schema als `Boolean?` exposed — Apollo serialisiert nullable
        // als `null`, NICHT als `false`. Truthy-Check (`if (allowMollie)`)
        // wuerde `false` korrekt, `null` aber falsch als "klassisch" einordnen
        // -- wir wollen Legacy-Bookings mit `null` aber genauso behandeln wie
        // explizit `false`. Daher strikter `=== true`.
        // Baustein 5 (mollie-treatment-follows-method): Strategy-Switch folgt der METHODE
        // (>=1 SP mit paymentProvider===Mollie), nicht mehr course.allowMolliePayment.
        const allowMolliePayment = bookingHasMolliePayment(booking?.scheduledPayments);

        return (
            <>
                {booking ? (
                    <div className={css.contentContainer}>
                        <ErasedRecordNotice erasedAt={booking.attendee?.erasedAt} />
                        <LateEntryPricingNotice
                            pricingReason={booking.pricingReason}
                            pricingCalculatedAt={booking.pricingCalculatedAt}
                            pricingSnapshot={booking.pricingSnapshot}
                        />
                        <PanelCardCourseData courseId={booking.courseLessons[0].course.id} lessons booking={booking} />
                        <div className={paymentMethodType !== "sepaDebit" ? css.twoColumns : undefined}>
                            <PanelCardBooker attendee={booking.attendee} />
                            <PanelCardPaymentData
                                booking={booking}
                                refetchScheduledPayments={async () => setTimeStamp(moment.unix(Date.now()).valueOf())}
                                isCanceled={isCanceled}
                                // BDSEC-06 — Mollie-Mode-Variante (hellblauer Bg + Logo + kein Edit-Button)
                                // aktivieren, wenn Strategy-Switch auf Mollie zeigt.
                                mollieMode={allowMolliePayment}
                            />
                        </div>
                        {allowMolliePayment ? (
                            <MolliePaymentSection
                                bookingId={booking.id}
                                scheduledPayments={booking.scheduledPayments ?? []}
                                // Field-Resolver (Plan 23-02) liefert die Liste in DESC-Order;
                                // ?? [] schuetzt vor `undefined` waehrend des ersten Query-Roundtrips.
                                molliePaymentAttempts={
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    ((booking as any)?.molliePaymentAttempts ?? [])
                                }
                                isCanceled={isCanceled}
                                isWaitlistBooking={bookingType === "Waitlist"}
                                refetchBooking={refetch}
                                bookingFullySettled={!isNotSettled}
                                // Empfängerzeile im Sammel-Link-Modal.
                                bookerEmail={booking.attendee?.booker?.email}
                                canManagePayments={isAdminOrHigher(highestRole)}
                                attendeeErased={Boolean(booking.attendee?.erasedAt)}
                            />
                        ) : (
                            <PanelCardPayments
                                bookingId={booking.id}
                                booking={booking}
                                scheduledPayments={booking.scheduledPayments}
                                timeStamp={timeStamp}
                                setTimeStamp={setTimeStamp}
                                isCanceled={isCanceled}
                                refetchBooking={refetch}
                            />
                        )}
                        <BookingDunningsPanel
                            bookingId={booking.id}
                            hasInvoice={Boolean(booking.invoice?.id)}
                            // Gebühren ändern die Raten: Buchung und die Ratenliste der Zahlungskarte neu laden
                            refetchBooking={() => {
                                void refetch();
                                setTimeStamp(moment.unix(Date.now()).valueOf());
                            }}
                        />
                        <BookingFeeLettersPanel bookingId={booking.id} hasInvoice={Boolean(booking.invoice?.id)} scheduledPayments={booking.scheduledPayments} />
                        <PanelCardNotes booking={booking} />
                        <PanelCard
                            title={<InvoiceHeader />}
                            columns={1}
                            gapAfter
                            extra={
                                <div style={{display: "flex", gap: 8}}>
                                    {invoiceData && !isCanceled && !booking.attendee?.erasedAt && (
                                        <CorrectBookingWizard
                                            bookingId={booking.id}
                                            invoiceId={invoiceData.id}
                                            expectedGrossPrice={booking.grossPrice}
                                            currentBasePrice={booking.grossPrice + (booking.discount ?? 0) + (booking.memberDiscount ?? 0) + (booking.creditedBalance ?? 0)}
                                            currentCreditedBalance={booking.creditedBalance ?? 0}
                                            availableCredit={booking.attendee?.booker?.credit ?? 0}
                                            // Wechsel der Zahlweise (#912) nur für Standard-Buchungen mit Einmalzahlung/Monatlich;
                                            // Einzelbuchungen (SettledLessonBookingDetails) übergeben den Prop bewusst nicht.
                                            paymentTypeSwitch={
                                                booking.paymentType === PaymentType.PrePaid || booking.paymentType === PaymentType.Monthly
                                                    ? {
                                                          current: booking.paymentType,
                                                          // Das Büro darf wechseln, die Kurs-Haken gelten nur für Kunden. Online-Zahlung bzw.
                                                          // Einzahlungsschein und Monatsraten schließen sich aus (Kursregel, Planner-Blocker).
                                                          allowMonthly: paymentMethodType !== "esr" && !booking.courseLessons[0]?.course?.allowMolliePayment && !allowMolliePayment,
                                                          allowUpFront: true,
                                                      }
                                                    : undefined
                                            }
                                        />
                                    )}
                                    <CorrectionInvoice bookingId={booking.id} invoiceId={invoiceData?.id} />
                                </div>
                            }
                        >
                            {invoiceData === undefined ? <div>{"-"}</div> : <RenderInvoicePreview invoiceData={invoiceData} />}
                        </PanelCard>
                        <CommunicationHistoryBooking bookingId={booking.id} />
                    </div>
                ) : null}
            </>
        );
    };

    if (renderWithoutPage) {
        return renderContent();
    }

    return (
        <Page
            content={
                <PageContentWrapper>
                    {loading ? null : (
                        <LayoutStep
                            columns={1}
                            cancel={false}
                            style={{
                                border: isCanceled ? `2px solid ${red[3]}` : "none",
                                backgroundColor: isCanceled ? "#F5F5F5" : "unset",
                                ...modalStyle,
                            }}
                            title={`${booking?.bookingNumberPrefix}-${booking?.bookingNumber}`}
                            subtitle={
                                <span
                                    style={{
                                        color: isCanceled ? red[4] : "unset",
                                        fontWeight: isCanceled ? 500 : "unset",
                                    }}
                                >{`${i18n.containers.common.BookingTypes[bookingType]()} ${subTextSecondPart}`}</span>
                            }
                            icon={<LeftMenuBookingIcon />}
                            button={
                                <Space>
                                    {/* Phase 23 / Plan 23-04: SendMailMenu erwartet den engeren
                                        GetBookingByIdQuery-Type — Booking ist hier um Mollie-Felder
                                        erweitert (BookingMolliePaymentAttemptsFieldsFragment). Downcast
                                        ist safe, weil mehr Felder als noetig vorhanden sind. */}
                                    <SendMailMenu booking={booking as unknown as GetBookingByIdQuery["bookingById"]} />
                                    <BookingHistoryDrawer bookingId={booking?.id} buttonProps={{size: "small", type: "link"}} />
                                    <MoreMenuSettlementDetails booking={booking} refetch={refetch} />
                                    <Button
                                        type="primary"
                                        size="small"
                                        // onClick={returnBack}
                                        onClick={() => {
                                            if (setModalOpen) {
                                                setModalOpen(false);
                                            } else {
                                                returnBack();
                                            }
                                        }}
                                    >
                                        {i18n.containers.courses.CourseDetails.close()}
                                    </Button>
                                </Space>
                            }
                        >
                            {renderContent()}
                        </LayoutStep>
                    )}
                </PageContentWrapper>
            }
        />
    );
};

export default memo(SettlementDetails);
